import { css } from '@emotion/react'
import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { t, Trans } from '@lingui/macro'
import { IconChatText } from '../../../../../components/common/icon/IconChatText'
import { SmallMarbleRating } from '../../../../../components/common/marble-rating/SmallMarbleRating'
import Spinner from '../../../../../components/common/loader/Loader'
import Tooltip from '../../../../../components/common/tooltip/Tooltip'
import UserAvatar from '../../../../../components/common/user/user-avatar/UserAvatar'
import { linkBuilder } from '../../../../../../../shared/linkBuilder'
import IconEnvelope from '../../../../../components/common/icon/IconEnvelope'
import {
  FeedbackEnrolmentItemFragment,
  useTopicFeedbackQuery
} from './ResourceFeedbackDrawer.codegen'
import { dayjsFromNow } from 'shared/dayjs'
import {
  Box,
  Flex,
  Link,
  Text,
  Heading,
  ButtonOutline
} from 'front-end/src/components'
import { brandColors } from '../../../../../../../shared/basicPalette'

type ResourceFeedbackDrawerProps = {
  topicId: number
}

const FEEDBACK_ITERATION_LIMIT = 10

export const ResourceFeedbackDrawerContent = (
  props: ResourceFeedbackDrawerProps
) => {
  const { topicId } = props
  const [enrolments, setEnrolments] = useState<FeedbackEnrolmentItemFragment[]>(
    []
  )
  const [loadedIterations, setLoadedIterations] = useState(1)
  const { data, loading } = useTopicFeedbackQuery({
    variables: {
      topicId,
      offset: (loadedIterations - 1) * FEEDBACK_ITERATION_LIMIT,
      limit: FEEDBACK_ITERATION_LIMIT
    },
    onCompleted: (data) => {
      if (data?.topic?.__typename === 'TopicModelAsEditor') {
        setEnrolments([...enrolments, ...data?.topic?.enrolments])
      }
    }
  })

  if (data?.topic?.__typename !== 'TopicModelAsEditor') {
    return null
  }

  if (enrolments.length === 0 && !loading) {
    return (
      <div className="no-items">
        <IconChatText boxSize="64px" mr="4px" />
        <Heading size="md">
          <Trans>No feedback yet</Trans>
        </Heading>
      </div>
    )
  }

  return (
    <>
      <ResourceFeedbackList enrolments={enrolments} />
      {loading && (
        <Box py="24px">
          <Spinner />
        </Box>
      )}
      {enrolments.length > 0 &&
        enrolments.length < data?.topic?.enrolmentsCount && (
          <Box p="12px">
            <ButtonOutline
              size="sm"
              onClick={() => setLoadedIterations(loadedIterations + 1)}
            >
              <Trans>Load more</Trans>
            </ButtonOutline>
          </Box>
        )}
    </>
  )
}

interface IResourceFeedbackListProps {
  enrolments: FeedbackEnrolmentItemFragment[]
}

const ResourceFeedbackList = ({ enrolments }: IResourceFeedbackListProps) => {
  const params = useParams<{
    feedbackTopicEnrolmentId: string
    topicId: string
  }>()

  useEffect(() => {
    if (params.feedbackTopicEnrolmentId) {
      const elm = document.getElementById(
        `feedback-${params.feedbackTopicEnrolmentId}`
      )
      elm && elm.scrollIntoView(true)
    }
  })

  return (
    <>
      {enrolments.map((enrolment, index) => (
        <ResourceFeedbackListItem key={index} enrolment={enrolment} />
      ))}
    </>
  )
}

interface IResourceFeedbackListItemProps {
  enrolment: FeedbackEnrolmentItemFragment
}

const ResourceFeedbackListItem = ({
  enrolment
}: IResourceFeedbackListItemProps) => {
  const { id, user, feedback, rating, completion_date, topicVersion } =
    enrolment

  const params = useParams<{
    feedbackTopicEnrolmentId: string
    topicId: string
  }>()

  return (
    <Box
      id={`feedback-${id}`}
      bgColor={brandColors.white}
      borderBottom={`1px solid ${brandColors.grey['200']}`}
      opacity={
        params.feedbackTopicEnrolmentId &&
        id !== Number(params.feedbackTopicEnrolmentId)
          ? '0.6'
          : undefined
      }
      css={css`
        .feedback-email-user {
          display: none;
        }
        &:hover .feedback-email-user {
          display: flex;
        }
      `}
    >
      <Flex as="header" justify="space-between" p="12px" align="center">
        <Flex align="center">
          <Link
            display="flex"
            alignItems="center"
            color={brandColors.grey['600']}
            href={linkBuilder.userDetail(user.id)}
            _hover={{
              textDecoration: 'none'
            }}
          >
            <UserAvatar
              // @ts-ignore
              user={user}
              size="small"
            />
            <Text ml="8px">{user.display_name}</Text>
          </Link>
          {user.email && (
            <Tooltip
              text={t`Email this user`}
              popoverProps={{
                placement: 'top'
              }}
            >
              <Link
                ml="8px"
                color={brandColors.grey['600']}
                href={`mailto: ${user.email}?subject=Feedback to ${topicVersion?.title} resource`}
                _hover={{
                  textDecoration: 'none'
                }}
                className="feedback-email-user"
              >
                <IconEnvelope />
              </Link>
            </Tooltip>
          )}
        </Flex>
        {rating && <SmallMarbleRating rating={rating} />}
      </Flex>
      <Text px="12px">{feedback}</Text>
      <Box
        pb="12px"
        pt="8px"
        px="12px"
        color={brandColors.grey.mid}
        fontSize="sm"
        as="footer"
      >
        {completion_date && (
          <Text
            as="span"
            _after={{
              content: '" • "'
            }}
          >
            {dayjsFromNow(completion_date)}
          </Text>
        )}
        <Trans>Version</Trans> {topicVersion?.version}
      </Box>
    </Box>
  )
}
